import { Fragment, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD } from '@aytm/lib/common/assets'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

import style from '@aytm/app/stats/page/question-views/info-view/components/charts/grid/styles/Block.module.scss'

// Info-view grid block: a ranked image grid (rank badge + image + text per cell).
// Replaces info_view/charts/grid.coffee (#info-view-question-block-grid-template + calcModelData).

const COLS_TO_SIZE = [390, 280, 200, 150]

// Rank sort + colour banding + column count, ported from calcModelData.
const layout = answerVariants => {
  const avs = answerVariants
    .map(av => ({ ...av }))
    .sort((a, b) => (a.rank || 1) + a.position / 100000 - ((b.rank || 1) + b.position / 100000))

  const max = avs.reduce((acc, av) => Math.max(acc, av.rank || 1), 1)
  avs.forEach(av => {
    av.color = max > 9 ? Math.ceil((9 * (av.rank || 1)) / max) : av.rank || 1
  })

  const cols = 4 === avs.length ? 2 : 0 === avs.length % 3 && 0 !== avs.length % 4 ? 3 : Math.min(avs.length, 4)

  return { avs, cols, rows: Math.ceil(avs.length / cols) }
}

const Block = ({ title, answerVariants }) => {
  const { avs, cols, rows } = useMemo(() => layout(answerVariants || []), [answerVariants])

  const w = 100 / cols
  const s = COLS_TO_SIZE[cols - 1]
  const cellStyle = { width: `${w}%`, maxWidth: `${w}%` }

  return (
    <BlockWrapper>
      <div className={style.component}>
        <div className={style.title}>{title}</div>
        <div className={style.body}>
          <div className={style.avGrid}>
            {Array.from({ length: rows }, (_, i) => (
              <Fragment key={i}>
                <div className={style.row}>
                  {Array.from({ length: cols }, (_, j) => {
                    const av = avs[i * cols + j]
                    if (!av) return null
                    return (
                      <div key={j} className={classNames(style.cell, style.image)} style={cellStyle}>
                        {av.image && av.image.original ? (
                          <div className={style.thumbnail} style={{ width: `${s}px`, height: `${s}px` }}>
                            <span className={classNames(style.rank, style[`color-${av.color}`])}>{av.rank || 1}</span>
                            <img
                              alt=""
                              src={getImageAssetPath({
                                path: av.image.original.path,
                                width: s,
                                height: s,
                                filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
                                backgroundColor: 'white'
                              })}
                            />
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
                <div className={style.row}>
                  {Array.from({ length: cols }, (_, j) => {
                    const av = avs[i * cols + j]
                    if (!av) return null
                    return (
                      <div key={j} className={classNames(style.cell, style.text)} style={cellStyle}>
                        <div className={style.textBox}>{av.text}</div>
                      </div>
                    )
                  })}
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  title: PropTypes.string,
  answerVariants: PropTypes.array
}

export default Block
